import { Layout, Input, Flex, Space, Tooltip, Typography, Button, theme, Form } from "antd";
import { HeaderApp, NavigationBar } from "../../shared";
import { PlusOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import HeaderPage from "../../shared/components/HeaderPage";
import EmptyData from "../../shared/components/EmptyData";
import { useFetchProducts } from "../hooks/useFetchProducts";
import Loading from "../../shared/components/Loading";
import { useNavigate } from "react-router-dom";
import ItemProduct from "../components/ItemProduct";
import DrawerDetails from "../../shared/components/DrawerDetails";
import { useState, useEffect } from "react";
import type { ProductData } from "../types";
import ProductDetails from "../components/ProductDetails";
import ProductForm from "../components/ProductForm";
import { useEditProduct } from "../hooks/useEditProduct";
import openNotification from "../../../utils/openNotification";

const Products: React.FC = () => {
	const { data: products, isLoading } = useFetchProducts();
	const navigate = useNavigate();
	const [selectedProduct, setSelectedProduct] = useState<ProductData | null>(null);
	const [editProduct, setEditProduct] = useState<ProductData | null>(null);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchText, setSearchText] = useState("");
	const { token } = theme.useToken();
	const [form] = Form.useForm();
	const { mutate: editProductMutation, isPending: isEditing } = useEditProduct();

	useEffect(() => {
		if (editProduct) {
			editProduct.product_key_sat = editProduct.product_key_sat.toString();
			form.setFieldsValue(editProduct);
		} else {
			form.resetFields();
		}
	}, [editProduct, form]);

	const handleEditFinish = async (values: ProductData) => {
		const payload = { ...values, id_product: editProduct?.id_product };
		editProductMutation(payload, {
			onSuccess: () => {
				openNotification('success', 'Producto actualizado exitosamente');
				setEditProduct(null);
			},
			onError: (error) => {
				openNotification('error', 'Error', error.message);
			}
		});
	};

	if (isLoading) {
		return <Loading />
	}

	const filteredProducts = products?.filter((c) => {
		if (!searchText) return true;
		const lowerSearch = searchText.toLowerCase();
		return (
			c.product_name.toLowerCase().includes(lowerSearch) ||
			c.product_key_sat.toLowerCase().includes(lowerSearch) ||
			(c.product_description?.toLowerCase().includes(lowerSearch) ?? false)
		);
	}) || [];

	return (
		<>
			<HeaderApp sectionName="Catálogos | Productos" showBack />
			<Layout.Content style={{ padding: "16px", width: "100%", backgroundColor: token.colorBgLayout }} className="max-width-container">
				<HeaderPage
					title="Productos"
					subtitle="Gestionar tus productos del sistema"
					actions={[
						{
							label: "Agregar producto",
							icon: <PlusOutlined />,
							type: "primary",
							onClick: () => {
								navigate("/catalogo/productos/nuevo");
							},
						},
					]}
				/>
				{/* List Header */}
				<Flex
					justify="space-between"
					align="center"
					style={{ marginBottom: 12, padding: "0 4px" }}
				>
					<Typography.Text strong style={{ fontSize: 15 }}>
						Listado
					</Typography.Text>
					<Space>
						{searchOpen && (
							<Input
								prefix={<SearchOutlined />}
								placeholder="Buscar producto..."
								size="small"
								autoFocus
								style={{ borderRadius: 8, width: 200 }}
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
								onBlur={() => {
									if (!searchText) setSearchOpen(false);
								}}
							/>
						)}
						<Tooltip title="Filtrar">
							<Button
								type="text"
								icon={<FilterOutlined />}
								shape="default"
								style={{ borderRadius: 8 }}
							/>
						</Tooltip>
						<Tooltip title="Buscar">
							<Button
								type="text"
								icon={<SearchOutlined />}
								shape="default"
								style={{ borderRadius: 8 }}
								onClick={() => setSearchOpen(true)}
							/>
						</Tooltip>
					</Space>
				</Flex>
				<main>
					{filteredProducts.length === 0 ?
						<EmptyData
							description="No hay productos registrados"
						/>
						: filteredProducts.map((product) => (
							<ItemProduct
								key={product.id_product}
								product={product}
								onClick={() => setSelectedProduct(product)}
								onEdit={() => setEditProduct(product)}
							/>
						))}
				</main>
				<DrawerDetails
					title="Detalles"
					onClose={() => {
						setSelectedProduct(null)
					}}
					open={!!selectedProduct}
					contentBody={
						selectedProduct ? <ProductDetails product={selectedProduct} /> : null
					}
				/>
				<DrawerDetails
					title="Editar Producto"
					onClose={() => setEditProduct(null)}
					open={!!editProduct}
					contentBody={
						editProduct ? (
							<div style={{ maxWidth: 640, margin: "0 auto" }}>
								<ProductForm
									form={form}
									onFinish={handleEditFinish}
									isPending={isEditing}
									buttonText="Actualizar Producto"
									formId="edit-product-form"
								/>
							</div>
						) : null
					}
				/>
				<NavigationBar activeKey="/providers" />
			</Layout.Content>
		</>
	);
};

export default Products;
